import { useEffect } from 'react';

interface GenieEmbedProps {
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
  /** Kept for backwards compatibility with existing callers — v2 doesn't use it. */
  buttonColor?: string;
  agentName?: string;
  customAvatarUrl?: string;
  subtitle?: string;
  peekoutText?: string;
}

declare global {
  interface Window {
    hg?: any;
    HelpGenieV2?: { init: (cfg: Record<string, unknown>) => void };
  }
}

/**
 * Marketing-page embed for Help Genie's own widget.
 *
 * index.html already injects the v2 widget globally for the whole app, so on
 * production this component is effectively idempotent — its guard sees
 * window.HelpGenieV2 already defined and bails. It exists as a defensive
 * fallback for pages/environments where the global embed didn't run (e.g.
 * isolated previews) and so the marketing pages keep a clear, prop-driven
 * surface for tuning the widget per-page.
 */
const GenieEmbed = ({
  position = 'bottom-right',
  primaryColor = '#6c8ff2',
  agentName = 'Genie Help',
  customAvatarUrl = 'https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/genie-images/s12rkeo-1754377048642.png',
  subtitle = 'Voice or text, your call',
  peekoutText = 'Got a question? Just ask',
}: GenieEmbedProps) => {
  useEffect(() => {
    if (window.HelpGenieV2) return;

    const origin = window.location.origin || 'https://helpgenie.ai';
    const script = document.createElement('script');
    script.src = origin + '/widget-v2.js';
    script.async = true;
    script.onload = () => {
      window.HelpGenieV2?.init({
        agentUrl: 'genie-help',
        baseUrl: origin,
        position,
        primaryColor,
        title: agentName,
        subtitle,
        avatarIconUrl: customAvatarUrl,
        peekoutText,
      });
    };
    document.head.appendChild(script);
  }, [position, primaryColor, agentName, customAvatarUrl, subtitle, peekoutText]);

  return null;
};

export default GenieEmbed;
